import { useEffect, useMemo, useRef } from 'react';

import { useAccountState } from '@/contexts/account/account-context';
import { useFetchPixelDestinations } from '@/features/workspace/hooks/use-fetch-pixel-destinations';
import * as Logger from '@/lib/monitoring/logger';
import { createPixelStore } from '@/features/workspace/stores/create-pixel-store';
import type { AccountStores, PixelDestinationsLoadingState } from '../types/account-stores-types';
import { AccountStoresContext, defaultPixelDestinationsLoadingState, PixelDestinationsLoadingContext } from './account-stores-context';

interface AccountStoresProviderProps {
  children: React.ReactNode;
}

function AccountStoresBootstrap({ children }: { children: React.ReactNode }) {
  const { accountId, pixelId } = useAccountState();
  const mountTime = useRef<number | null>(null);
  if (mountTime.current === null) {
    mountTime.current = performance.now();
  }
  const hasLoggedMount = useRef(false);
  const hasLoggedReady = useRef(false);

  useEffect(() => {
    if (!hasLoggedMount.current) {
      hasLoggedMount.current = true;
      Logger.debug('AccountStoresBootstrap mounted', {
        accountId,
        pixelId,
      });
    }
  }, []);

  const {
    isLoading: destinationsLoading,
    isFetching: destinationsFetching,
    isSuccess: destinationsSuccess,
    isError: destinationsError,
    refetch: destinationsRefetch,
  } = useFetchPixelDestinations();

  const pixelDestinationsLoading = useMemo<PixelDestinationsLoadingState>(
    () => ({
      isLoading: destinationsLoading,
      isFetching: destinationsFetching,
      isSuccess: destinationsSuccess,
      isError: destinationsError,
      refetch: () => {
        void destinationsRefetch();
      },
    }),
    [
      destinationsLoading,
      destinationsFetching,
      destinationsSuccess,
      destinationsError,
      destinationsRefetch,
    ]
  );

  useEffect(() => {
    if (!hasLoggedReady.current && destinationsSuccess) {
      hasLoggedReady.current = true;
      const durationMs = Math.round(performance.now() - (mountTime.current ?? 0));
      Logger.debug('AccountStoresBootstrap ready', {
        accountId,
        pixelId,
        durationMs,
        destinationsLoaded: destinationsSuccess,
      });
    }
  }, [destinationsSuccess, accountId, pixelId]);

  return (
    <PixelDestinationsLoadingContext value={pixelDestinationsLoading}>
      {children}
    </PixelDestinationsLoadingContext>
  );
}

export function AccountStoresProvider({ children }: AccountStoresProviderProps) {
  const { accountId } = useAccountState();
  const hasLoggedMount = useRef(false);

  const stores = useMemo<AccountStores | null>(() => {
    if (!accountId) {
      return null;
    }

    Logger.debug('AccountStoresProvider creating stores', { accountId });
    return {
      pixel: createPixelStore(accountId),
    };
  }, [accountId]);

  useEffect(() => {
    if (!hasLoggedMount.current) {
      hasLoggedMount.current = true;
      Logger.debug('AccountStoresProvider mounted', {
        accountId,
        hasStores: !!stores,
      });
    }
  }, []);

  return (
    <AccountStoresContext value={stores}>
      {accountId ? (
        <AccountStoresBootstrap>{children}</AccountStoresBootstrap>
      ) : (
        <PixelDestinationsLoadingContext value={defaultPixelDestinationsLoadingState}>
          {children}
        </PixelDestinationsLoadingContext>
      )}
    </AccountStoresContext>
  );
}
